// Column filters: the ticked-value list and the condition expression.
//
// Two ways of saying the same kind of thing, because they answer different
// questions. A list answers "which of these do I want", which needs no syntax
// and shows you what is there. An expression answers "everything over 5000",
// which a list of forty distinct prices cannot. Google Sheets has both and so
// does this.
//
// They AND together when both are set: unticking Retail and typing >1000 means
// rows over 1000 that are not Retail.

import type { CellValue, Sheet } from './types';

export interface ColumnFilter {
  /**
   * Values unticked in the list, compared as the text the cell DISPLAYS.
   *
   * Excluded rather than included, so a value that appears after the filter was
   * set is visible by default — an included-list filter silently hides new data,
   * which is how a total quietly stops matching the sheet it came from.
   */
  hidden?: string[];
  /** A condition on the cell: ">1000", "~tower", ">=100 and <500". */
  expr?: string;
}

export type FilterOp = '=' | '!=' | '>' | '<' | '>=' | '<=' | '~' | '!~';

export interface Condition {
  op: FilterOp;
  value: string;
}

export interface ParsedCondition {
  /** OR of ANDs: [[a, b], [c]] is "(a and b) or c". */
  groups: Condition[][];
  error: string | null;
}

/**
 * Row 1 is the header and is never filtered out.
 *
 * Every sheet this app produces has one, and a filter that can hide the labels
 * it is filtering by is a filter nobody can turn off.
 */
export const FIRST_FILTERABLE_ROW = 2;

const OPS: FilterOp[] = ['>=', '<=', '!=', '!~', '=', '>', '<', '~'];

/**
 * The last row that holds anything.
 *
 * The value LIST is built from the data, not from the two hundred blank rows
 * after it — otherwise it reads "(blank) × 195" above the four values anyone
 * cares about. The filter itself still applies to every row on the sheet: a
 * blank row is blank whether it is row 40 or row 200, and hiding some of them
 * and not others is not a thing anyone asked for.
 */
export function lastContentRow(sheet: Sheet): number {
  let last = 0;
  for (const key of Object.keys(sheet.cells)) {
    const r = Number(key.slice(0, key.indexOf(',')));
    if (r > last) last = r;
  }
  return last;
}

/**
 * Parse a condition.
 *
 * A bare word with no operator means "contains", so typing `tower` does the
 * obvious thing rather than an error. `<>` is accepted for `!=` because Excel
 * users type it.
 */
export function parseCondition(expr: string | null | undefined): ParsedCondition {
  const groups: Condition[][] = [];
  const text = (expr ?? '').trim();
  if (text === '') return { groups, error: null };

  for (const orPart of text.split(/\s+or\s+/i)) {
    const group: Condition[] = [];
    for (const andPart of orPart.split(/\s+and\s+/i)) {
      const clause = andPart.trim();
      if (clause === '') continue;
      const normalized = clause.startsWith('<>') ? `!=${clause.slice(2)}` : clause;
      const op = OPS.find((o) => normalized.startsWith(o));
      if (op) {
        group.push({ op, value: unquote(normalized.slice(op.length).trim()) });
      } else {
        group.push({ op: '~', value: unquote(clause) });
      }
    }
    if (group.length > 0) groups.push(group);
  }
  if (groups.length === 0) return { groups, error: `cannot read "${text}"` };
  return { groups, error: null };
}

function unquote(s: string): string {
  if (s.length >= 2 && (s[0] === '"' || s[0] === "'") && s[s.length - 1] === s[0]) {
    return s.slice(1, -1);
  }
  return s;
}

/**
 * Test one cell.
 *
 * Compared as numbers when both sides are numbers, and as case-insensitive text
 * otherwise — "5000" typed into the box has to match the number 5000, and
 * "retail" has to match "Retail".
 */
export function testCondition(parsed: ParsedCondition, value: CellValue | null, text: string): boolean {
  if (parsed.groups.length === 0) return true;
  return parsed.groups.some((group) => group.every((c) => testOne(c, value, text)));
}

function testOne(condition: Condition, value: CellValue | null, text: string): boolean {
  const { op, value: raw } = condition;
  const shown = text.trim();
  const lower = shown.toLowerCase();
  const wanted = raw.trim().toLowerCase();

  if (op === '~') return lower.includes(wanted);
  if (op === '!~') return !lower.includes(wanted);

  const a = numberOf(value, shown);
  const b = raw.trim() === '' ? null : Number(raw.trim());
  const numeric = a !== null && b !== null && Number.isFinite(b);

  switch (op) {
    case '=':
      return numeric ? a === b : lower === wanted;
    case '!=':
      return numeric ? a !== b : lower !== wanted;
    case '>':
      return numeric ? a > b! : lower > wanted;
    case '<':
      return numeric ? a < b! : lower < wanted;
    case '>=':
      return numeric ? a >= b! : lower >= wanted;
    case '<=':
      return numeric ? a <= b! : lower <= wanted;
    default:
      return true;
  }
}

/** The number a cell holds, seeing through a formatted display like "$8,700". */
function numberOf(value: CellValue | null, text: string): number | null {
  if (typeof value === 'number') return value;
  if (text === '') return null;
  const stripped = text.replace(/[^0-9.eE+-]/g, '');
  if (stripped === '') return null;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : null;
}

export interface CellRead {
  value: CellValue | null;
  text: string;
}

/**
 * Which rows every filter on the sheet agrees to hide.
 *
 * A row is out if ANY column's filter rejects it — the same as every
 * spreadsheet, and the only reading under which adding a second filter narrows
 * rather than widens.
 */
export function filteredRows(
  sheet: Sheet,
  read: (row: number, col: number) => CellRead
): Set<number> {
  const out = new Set<number>();
  const filters = sheet.filters;
  if (!filters) return out;

  const active = Object.entries(filters)
    .map(([col, filter]) => ({
      col: Number(col),
      hidden: filter.hidden && filter.hidden.length > 0 ? new Set(filter.hidden) : null,
      parsed: parseCondition(filter.expr),
    }))
    .filter((f) => f.hidden !== null || f.parsed.groups.length > 0);
  if (active.length === 0) return out;

  for (let r = FIRST_FILTERABLE_ROW; r <= sheet.rows; r++) {
    for (const f of active) {
      const cell = read(r, f.col);
      if (f.hidden?.has(cell.text)) {
        out.add(r);
        break;
      }
      if (!testCondition(f.parsed, cell.value, cell.text)) {
        out.add(r);
        break;
      }
    }
  }
  return out;
}

/** Whether a column has anything filtering it — drives the header marker. */
export function isFiltered(sheet: Sheet, col: number): boolean {
  const f = sheet.filters?.[col];
  if (!f) return false;
  return (f.hidden !== undefined && f.hidden.length > 0) || parseCondition(f.expr).groups.length > 0;
}

export function anyFilter(sheet: Sheet): boolean {
  const filters = sheet.filters;
  if (!filters) return false;
  return Object.keys(filters).some((c) => isFiltered(sheet, Number(c)));
}
